<template>
    <ui-modal
        :name="$mx_getModalName"
        :load-success="loader.modalLoadSuccess"
        :load="loader.modalLoad"
        :title="titleModal"
        icon="fal fa-comment-alt-lines"
        size="xs"
    >
        <div v-if="customerChangeOption" class="modal-content broker-area">
            <ui-sponsor-selector
                v-if="!$mx_userIsBroker"
                v-model="form.data.sponsor"
                @input="customerHandler"
            />

            <ui-deal-selector
                v-model="customerDeal"
                :label="form.fields.customer_id.label + ':'"
                :data="form.data.sponsor"
                :disabled="selectCustomerOptionsCondition"
                return-value="object"
                @input="customerInfoHandler"
            />
        </div>

        <div class="historical-area">
            <ui-text-with-select-area title="Como foi o atendimento?">
                <template #select>
                    <ui-select-2
                        v-model="form.historical.type"
                        :show-footer="false"
                        :data="historicalTypesOptions"
                        :loading="loader.historicalTypesLoad"
                        :disabled="disabledHistoricalCondition"
                        align="right"
                        placeholder="Tipo"
                        design="mgb-0"
                        item-label="label"
                        return-value="value"
                        :search="false"
                        size="xs"
                    />
                </template>
                <template #text>
                    <textarea
                        v-model="form.historical.message"
                        :disabled="disabledHistoricalCondition"
                        placeholder="Digite..."
                        rows="6"
                    />
                </template>
            </ui-text-with-select-area>
        </div>

        <template #footer>
            <div class="cancel-buttons">
                <ui-modal-cancel-button :modal-name="$mx_getModalName" :track-event="gtm.cancel"
                >Cancelar</ui-modal-cancel-button
                >
            </div>
            <ui-button
                :disabled="!canSubmit"
                type="button"
                design="md success mw-100"
                @click="submit"
            >Salvar</ui-button
            >
        </template>
    </ui-modal>
</template>

<script>
	import { task, dealHistorical } from '@/services/axios'
	import { mapGetters } from 'vuex'

	import { uiModal, uiModalCancelButton } from '@/components/ui/modal'
	import { uiButton } from '@/components/ui/buttons'
	import { uiSelect2, uiTextWithSelectArea } from '@/components/ui/form'
	import { uiSponsorSelector, uiDealSelector } from '@/components/ui-facilita/selectors'

	import { loaderMixin, alertMixin, modalMixin, utilMixin } from '@/mixins'

	import { defaultGtm } from './default'

	const defaultFormData = {
		id: null,
		sponsor: null,
		customer_id: null,
		allow_fill: 0,
	}

	const defaultHistoricalData = {
		message: null,
		type: null,
	}

	const defaultLoader = {
		modalLoad: false,
		modalLoadSuccess: false,
		historicalTypesLoad: false,
	}

	export default {
		name: 'ui-deal-create-historical-modal',

		components: {
			uiModal,
			uiModalCancelButton,
			uiTextWithSelectArea,
			uiSelect2,
			uiButton,
			uiSponsorSelector,
			uiDealSelector,
		},

		mixins: [loaderMixin, alertMixin, modalMixin, utilMixin],

		props: {
			name: {
				type: String,
				required: true,
			},
			customerChangeOption: {
				type: Boolean,
				default: false,
			},
		},

		data() {
			return {
				form: {
					fields: {
						sponsor: {
							label: 'Responsável',
							required: true,
							disabled: false,
						},
						customer_id: {
							label: 'Cliente',
							required: true,
							disabled: false,
						},
					},
					data: { ...defaultFormData },
					historical: { ...defaultHistoricalData },
				},
				loader: { ...defaultLoader },
				customerDeal: {},
				staticCustomer: {},
				historicalTypesOptions: [],
				userList: [],
				gtm: { ...defaultGtm() },
			}
		},

		computed: {
			...mapGetters('auth', { user: 'getUser' }),

			/**
			 * Dados computados de uso geral:
			 */

			getDealId() {
				return this.form.data.id
			},

			titleModal() {
				const id = this.form.data.id ? ' – #' + this.form.data.id : ''
				return 'Registrar Atendimento' + id
			},

			selectCustomerOptionsCondition() {
				return (
					this.form.fields.customer_id.disabled ||
					!this.$mx_hasValue(this.form.data.sponsor)
				)
			},

			disabledHistoricalCondition() {
				return (
					this.loader.historicalTypesLoad ||
					(this.customerChangeOption &&
						(!this.$mx_hasValue(this.form.data.customer_id) ||
							!this.$mx_hasValue(this.form.data.sponsor)))
				)
			},

			/**
			 * Condições responsáveis por analisar se os dados foram preenchidos
			 */
			hasADealAndAllowFill() {
				return (
					this.$mx_hasValue(this.form.data.id) &&
					this.$mx_hasValue(this.form.data.allow_fill)
				)
			},

			hasAHistorical() {
				return this.form.historical.message && this.$mx_hasValue(this.form.historical.type)
			},

			hasLoadingInForm() {
				return this.loader.historicalTypesLoad
			},

			canSubmit() {
				return (
					!this.hasLoadingInForm &&
					!this.disabledHistoricalCondition &&
					this.hasADealAndAllowFill &&
					this.hasAHistorical
				)
			},

			validateFormHistoricalData() {
				const type = this.form.historical.type
				if (this.$mx_hasValue(type))
					return {
						subject: 'Registro de Atendimento: ' + this.historicalTypeTitle(type),
						type: type,
						message: this.form.historical.message,
						deal_id: this.getDealId,
					}
				else return null
			},

			getCustomerNameWithDealId() {
				const sc = this.staticCustomer
				return sc.customer_id + ' - ' + sc.name
			},
		},

		mounted() {
			this.$root.$on('modal-open-' + this.$mx_getModalName, async (payload) => {
				this.reset()

				const formData = this.form.data

				/**
				 * Verificação da propriedade que define
				 * se o cliente será modificável ou não.
				 */
				if (!this.customerChangeOption && payload) {
					formData.id = payload.id
					formData.allow_fill = payload.allow_fill

					this.staticCustomerHandler(payload)
					formData.customer_id = payload.customer_id

					await this.historicalTypesHandler()
					//...
				} else {
					await this.historicalTypesHandler()

					if (this.$mx_userIsBroker)
						this.form.data.sponsor = {
							group: this.user.group,
							id: this.user.broker_id,
						}
				}

				this.userList = []

				this.form.historical.type = 'observation'
			})
		},

		beforeDestroy() {
			this.$mx_closeModal()
		},

		methods: {
			/**
			 * Getters responsáveis por buscar Clientes, Etapas e Status.
			 */

			getInfoAboutCustomerDeal() {
				const customer = this.customerDeal
				if (this.$mx_hasValue(customer))
					return {
						id: customer.id,
						stageId: customer.funnel_stage_id,
						stageStatusId: customer.funnel_stage_status_id,
						customerId: customer.customer_id,
						allow_fill: customer.allow_fill,
					}
				return false
			},

			/**
			 * Handlers responsáveis por lidar com os Clientes, Etapas e Status.
			 */

			staticCustomerHandler(deal) {
				this.staticCustomer = { ...this.staticCustomerFactory(deal) }
			},

			staticCustomerFactory(data) {
				return {
					customer_id: data.customer_id,
					name: data.customer_name,
				}
			},

			customerHandler(sponsor) {
				if (!this.$mx_hasValue(sponsor)) this.form.data.sponsor = null
				this.customerDeal = {}
				this.form.data.id = null
			},

			async customerInfoHandler() {
				const info = this.getInfoAboutCustomerDeal()
				if (this.$mx_hasValue(info.id)) {
					this.form.data.id = info.id
					this.form.data.customer_id = info.customerId
					this.form.data.allow_fill = parseInt(info.allow_fill)
				}
			},

			async getHistoricalTypes() {
				try {
					const types = await task.getAll('types', null)
					return [...types.data[0].fields]
				} catch (error) {
					this.$mx_alertThisOnError(error)
					return []
				}
			},

			async historicalTypesHandler() {
				if (!this.historicalTypesOptions.length) {
					this.$mx_setLoader('historicalTypesLoad', true)
					this.historicalTypesOptions = await this.getHistoricalTypes()
					this.$mx_setLoader('historicalTypesLoad', false)
				}
			},

			historicalTypeTitle(type) {
				return this.historicalTypesOptions.find((his) => his.value == type).label
			},

			closeAnimation() {
				setTimeout(() => {
					this.$mx_setLoader('modalLoadSuccess', true)

					setTimeout(() => {
						this.$mx_closeModal()
						this.$mx_reloadTablePage()
					}, 1000)
				}, 500)
			},

			reset() {
				this.form.data = { ...defaultFormData }
				this.form.historical = { ...defaultHistoricalData }
				this.loader = { ...defaultLoader }
				this.customerDeal = {}
				this.staticCustomer = {}
			},

			async submit() {
				if (!this.canSubmit) return
				this.$mx_setLoader('modalLoad', true)

				try {
					await dealHistorical.create('', this.validateFormHistoricalData)
					this.$gtm.trackEvent(this.gtm.submit)
					this.closeAnimation()
					// ...
				} catch (error) {
					this.$mx_alertThisOnError(error)
					this.$mx_setLoader('modalLoad', false)
				}
			},
		},
	}
</script>

<style lang="stylus" scoped>
	@import 'styles.styl'
</style>
